import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
// Import our custom Zod helper
import zodHelper from "@/lib/zod-helper";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  InquiryFormData,
  InquiryDayItem,
  InquiryHotelItem,
  TourPackageItem,
} from "@/types/database";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import { InquiryDayForm } from "./inquiry-day-form";
import { InquiryHotelForm } from "./inquiry-hotel-form";
import { InquiryTotalSummary } from "./inquiry-total-summary";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUserRole } from "@/lib/useUserRole";

const formSchema = zodHelper.object({
  guest_name: zodHelper.string().min(1, { message: "Guest name is required" }),
  start_date: zodHelper.date({
    required_error: "Date is required",
  }),
  adult_count: zodHelper.coerce.number().min(1, { message: "Adult count must be at least 1" }),
  child_count: zodHelper.coerce.number().min(0),
  nights: zodHelper.coerce.number().min(1, { message: "Nights must be at least 1" }),
  status: zodHelper.enum(["draft", "new", "sent", "confirmed"]),
  hotel_type: zodHelper.enum(["Direct Booking", "Include Hotel", "No Hotel"]),
  car_type: zodHelper.enum(["MPV", "ELF Short", "Hiace", "ELF Giga", "Bus"]),
  remarks: zodHelper.string().optional(),
  flight_information: zodHelper.string().nullish(),
});

interface InquiryFormProps {
  initialData?: Partial<InquiryFormData>;
  onSubmit: (data: any) => void;
}

export function InquiryForm({ initialData, onSubmit }: InquiryFormProps) {
  // Get user role to determine if status editing is allowed
  const { role } = useUserRole();
  const canEditStatus = role === 'admin' || role === 'agent';

  const form = useForm<any>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      guest_name: initialData?.guest_name || "",
      start_date: initialData?.start_date ? new Date(initialData.start_date) : undefined,
      adult_count: initialData?.adult_count || 1,
      child_count: initialData?.child_count || 0,
      nights: initialData?.nights || 1,
      status: initialData?.status || "new",
      hotel_type: initialData?.hotel_type || "Direct Booking",
      car_type: initialData?.car_type || "MPV",
      remarks: initialData?.remarks || "",
      flight_information: initialData?.flight_information ?? "",
    },
  });

  const [days, setDays] = useState<InquiryDayItem[]>(
    initialData?.days || [
      {
        id: "",
        created_at: "",
        updated_at: "",
        inquiry_id: "",
        day_number: 1,
        day_date: "",
        tour_package_id: "",
        tour_package_name: "",
        transport_area: "Kuta",
        transport_cost: 0,
        parking_cost: 0,
        meal_cost: 0,
        other_cost: 0,
        car_type: "MPV",
        captured_tours: {},
        is_edited: false,
        note: "",
      },
    ]
  );

  const [hotels, setHotels] = useState<InquiryHotelItem[]>(
    initialData?.hotels || []
  );

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [formValues, setFormValues] = useState<any>(null);
  const [totalUSD, setTotalUSD] = useState<number | undefined>(initialData?.total_usd);

  const { toast } = useToast();

  const formatIDR = (value: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Get tour packages for the dropdown
  const { data: tourPackages = [] } = useQuery({
    queryKey: ["tourPackages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tour_package")
        .select("id, tour_package_name")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as unknown as TourPackageItem[];
    },
  });

  // Update days when night changes
  useEffect(() => {
    const nights = form.watch("nights");
    if (nights > 0) {
      setDays((prevDays) => {
        const newDays = [...prevDays];
        // Add days if needed
        while (newDays.length < nights) {
          newDays.push({
            id: "",
            created_at: "",
            updated_at: "",
            inquiry_id: "",
            day_number: newDays.length + 1,
            day_date: "",
            tour_package_id: "",
            tour_package_name: "",
            transport_area: "Kuta",
            transport_cost: 0,
            parking_cost: 0,
            meal_cost: 0,
            other_cost: 0,
            car_type: "MPV",
            captured_tours: {},
            is_edited: false,
            note: "",
          });
        }
        // Remove days if needed
        while (newDays.length > nights) {
          newDays.pop();
        }
        return newDays;
      });
    }
  }, [form.watch("nights")]);

  // Update car_type for all days when the main car_type changes
  useEffect(() => {
    const carType = form.watch("car_type") as "MPV" | "ELF Short" | "Hiace" | "ELF Giga" | "Bus";
    if (carType) {
      setDays((prevDays) => {
        return prevDays.map((day) => ({
          ...day,
          car_type: carType,
        }));
      });
    }
  }, [form.watch("car_type")]);

  // Update day dates based on the start date
  useEffect(() => {
    const startDate = form.watch("start_date");
    if (startDate && startDate instanceof Date) {
      setDays((prevDays) => {
        return prevDays.map((day, index) => {
          // Clone the start date and add the index days to it
          const dayDate = new Date(startDate);
          dayDate.setDate(dayDate.getDate() + index);

          return {
            ...day,
            day_date: format(dayDate, "yyyy-MM-dd"),
          };
        });
      });
    } else {
      // If no start date is selected, leave day_date blank
      setDays((prevDays) => {
        return prevDays.map((day) => ({
          ...day,
          day_date: "",
        }));
      });
    }
  }, [form.watch("start_date")]);

  const calculateTotals = (
    days: InquiryDayItem[],
    hotels: InquiryHotelItem[],
    adult_count: number,
    child_count: number
  ) => {
    // Calculate transport total
    const totalTransport = days.reduce((sum, day) => sum + (day.transport_cost || 0), 0);

    // Calculate parking from captured_tours
    const totalParking = days.reduce((sum, day) => {
      if (!day.captured_tours || !day.captured_tours[`day_${day.day_number}`])
        return sum;
      const dayTours = day.captured_tours[`day_${day.day_number}`];
      return sum + Object.values(dayTours.parking).reduce((s, cost) => s + (cost || 0), 0);
    }, 0);

    // Calculate entrance fees from captured_tours
    const { totalAdultEntrance, totalChildEntrance } = days.reduce((acc, day) => {
      if (!day.captured_tours || !day.captured_tours[`day_${day.day_number}`])
        return acc;
      const dayTours = day.captured_tours[`day_${day.day_number}`];

      dayTours.tours.forEach((tour) => {
        acc.totalAdultEntrance += (dayTours.entrance_adult[tour] || 0) * adult_count;
        acc.totalChildEntrance += (dayTours.entrance_child[tour] || 0) * child_count;
      });

      return acc;
    }, { totalAdultEntrance: 0, totalChildEntrance: 0 });

    const totalEntrance = totalAdultEntrance + totalChildEntrance;
    const totalMealCost = days.reduce((sum, day) => sum + (day.meal_cost || 0), 0);
    const totalOtherCost = days.reduce((sum, day) => sum + (day.other_cost || 0), 0);
    const totalHotelPrice = hotels.reduce((sum, hotel) => sum + (hotel.total_price || 0), 0);
    const grandTotal = totalTransport + totalParking + totalEntrance + totalMealCost + totalOtherCost + totalHotelPrice;

    return {
      totalTransport,
      totalParking,
      totalAdultEntrance,
      totalChildEntrance,
      totalEntrance,
      totalMealCost,
      totalOtherCost,
      totalHotelPrice,
      grandTotal,
    };
  };

  const handleSubmit = async (values: any) => {
    // Validate that all days have required data
    const hasIncompleteDays = days.some((day) => {
      // Check if day_date is missing
      if (!day.day_date) {
        toast({
          title: "Missing Date",
          description: `Please set a date for Day ${day.day_number}`,
          variant: "destructive",
        });
        return true;
      }

      // Check if tour package is selected
      if (!day.tour_package_id) {
        toast({
          title: "Missing Tour Package",
          description: `Please select a tour package for Day ${day.day_number}`,
          variant: "destructive",
        });
        return true;
      }

      // Check if transport area is set
      if (!day.transport_area) {
        toast({
          title: "Missing Transport Area",
          description: `Please select a transport area for Day ${day.day_number}`,
          variant: "destructive",
        });
        return true;
      }

      // Check if captured_tours data is complete
      if (!day.captured_tours || !day.captured_tours[`day_${day.day_number}`]) {
        toast({
          title: "Incomplete Tour Data",
          description: `Tour data for Day ${day.day_number} is incomplete. Please reselect the tour package.`,
          variant: "destructive",
        });
        return true;
      }

      return false;
    });

    if (hasIncompleteDays) {
      return;
    }

    // Store form values and show confirmation dialog
    setFormValues(values);
    setShowConfirmDialog(true);
  };

  const confirmSubmit = () => {
    if (!formValues) return;

    // Get totals from the summary component
    const {
      totalTransport,
      totalParking,
      totalAdultEntrance,
      totalChildEntrance,
      totalEntrance,
      totalMealCost,
      totalOtherCost,
      totalHotelPrice,
      grandTotal,
    } = calculateTotals(days, hotels, formValues.adult_count, formValues.child_count);

    // Convert days array to tour package JSON
    const tour_package_json = days.reduce((acc, day) => {
      if (day.captured_tours) {
        const dayKey = `day_${day.day_number}`;
        const dayData = day.captured_tours[dayKey];

        // Calculate total parking fee for this day
        const totalParking = dayData?.tours.reduce((sum, tour) => {
          return sum + ((dayData?.parking && dayData.parking[tour]) || 0);
        }, 0);

        acc[dayKey] = {
          title: `Day ${day.day_number} (${day.day_date || ""}) - ${
            dayData?.tour_package_name || ""
          }`,
          tours: dayData?.tours || [],
          entrance_adult: dayData?.entrance_adult || {},
          entrance_child: dayData?.entrance_child || {},
          parking: dayData?.parking || {},
          transport_area: day.transport_area,
          transport_cost: day.transport_cost,
          meal_cost: day.meal_cost,
          other_cost: day.other_cost,
          tour_package_id: day.tour_package_id,
          tour_package_name: dayData?.tour_package_name || "",
          itinerary: dayData?.itinerary || "",
          day_date: day.day_date || "",
          note: day.note || "",
        };
      }
      return acc;
    }, {} as Record<string, any>);

    // Convert hotels array to hotel JSON
    const hotel_json = {
      hotels: hotels.reduce((acc, hotel, index) => {
        acc[`day_${index + 1}`] = {
          hotel_name: hotel.hotel_name,
          checkin_date: format(new Date(hotel.checkin_date), "yyyy-MM-dd"),
          checkout_date: format(new Date(hotel.checkout_date), "yyyy-MM-dd"),
          nights: hotel.nights,
          room_rate: hotel.room_rate,
          room_count: hotel.room_count,
          total_price: hotel.total_price,
        };
        return acc;
      }, {} as Record<string, any>),
    };

    // Submit only the required data
    onSubmit({
      guest_name: formValues.guest_name,
      start_date: format(formValues.start_date, "yyyy-MM-dd"),
      adult_count: formValues.adult_count,
      child_count: formValues.child_count,
      nights: formValues.nights,
      status: formValues.status,
      hotel_type: formValues.hotel_type,
      car_type: formValues.car_type,
      remarks: formValues.remarks,
      flight_information: formValues.flight_information,
      tour_package_json,
      hotel_json,
      total_transport_cost: totalTransport,
      total_parking_cost: totalParking,
      total_adult_entrance_fee: totalAdultEntrance,
      total_child_entrance_fee: totalChildEntrance,
      total_entrance_fee: totalEntrance,
      total_meal_cost: totalMealCost,
      total_other_cost: totalOtherCost,
      total_hotel_price: totalHotelPrice,
      grand_total: grandTotal,
      total_usd: totalUSD,
      days,
      hotels,
    });

    // Close the dialog
    setShowConfirmDialog(false);
  };

  const handleDayChange = (index: number, updatedDay: InquiryDayItem) => {
    const newDays = [...days];
    newDays[index] = updatedDay;
    setDays(newDays);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Guest Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-6 gap-4">
              <FormField
                control={form.control}
                name="guest_name"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Guest name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="adult_count"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Adult</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="child_count"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Child</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="nights"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Days</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="start_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant={"outline"}
                            className={cn(
                              "w-full pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "dd MMM yyyy")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) =>
                            date < new Date("1900-01-01")
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select 
                      onValueChange={(value) => {
                        if (!canEditStatus) return;
                        field.onChange(value);
                      }} 
                      defaultValue={field.value}
                      disabled={!canEditStatus}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="new">New</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="hotel_type"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Hotel Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select hotel type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Direct Booking">Direct Booking</SelectItem>
                        <SelectItem value="Include Hotel">Include Hotel</SelectItem>
                        <SelectItem value="No Hotel">No Hotel</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="car_type"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel className="text-xs">Car Type</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue placeholder="Select car type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="MPV" className="text-xs">MPV</SelectItem>
                        <SelectItem value="ELF Short" className="text-xs">ELF Short</SelectItem>
                        <SelectItem value="Hiace" className="text-xs">Hiace</SelectItem>
                        <SelectItem value="ELF Giga" className="text-xs">ELF Giga</SelectItem>
                        <SelectItem value="Bus" className="text-xs">Bus</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="remarks"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Remarks</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Additional notes..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="flight_information"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Flight Information</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Flight information..."
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Day by Day Tour Package */}
        <Card>
          <CardHeader>
            <CardTitle>Day by Day Tour Package</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {days.map((day, index) => (
                <div key={day.day_number} className="bg-white rounded-lg border shadow-sm p-4">
                  <InquiryDayForm
                    day={{ ...day, car_type: form.watch("car_type") }}
                    adult={form.watch("adult_count")}
                    child={form.watch("child_count")}
                    onUpdate={(updatedDay) => {
                      const newDays = [...days];
                      newDays[index] = updatedDay;
                      setDays(newDays);
                    }}
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Hotel Information */}
        <Card>
          <CardHeader>
            <CardTitle>Hotel Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <InquiryHotelForm
                hotels={hotels}
                onChange={(newHotels) => setHotels(newHotels)}
                nights={form.watch("nights")}
              />
            </div>
          </CardContent>
        </Card>

        {/* Grand Total Summary */}
        <InquiryTotalSummary
          days={days}
          hotels={hotels}
          adult_count={form.watch("adult_count")}
          child_count={form.watch("child_count")}
          totalUSD={totalUSD}
          onChangeTotalUSD={(value) => setTotalUSD(value)}
        />

        <Button type="submit" className="w-full">
          Submit
        </Button>
      </form>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Save</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to save this inquiry? Please review all information before confirming.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-2">
            <AlertDialogCancel className="mt-0">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmSubmit} className="bg-primary">
              Yes, Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Form>
  );
}